import React, { ReactNode, useEffect, useState } from "react";
import { Box, Grid, MenuItem, Paper, Select } from "@mui/material";
import { JiraAutoConfigurationSection } from "~/pages/integrations/hosted-integrations/forms/jira/JiraAutoConfigurationSection";
import { JiraFormInput } from "~/pages/integrations/hosted-integrations/forms/jira/JiraIntegrationForm/JiraIntegrationForm";
import {
  ClientIntegrationType,
  GetClientIntegrationDocument,
  IntegrationType,
  useUpdateClientIntegrationConfigurationMutation,
} from "~/operations";
import { Controller, useForm } from "react-hook-form";
import { Space } from "~/lib/types";
import { TicketIntegration } from "~/pages/tickets/components/TicketSettings/types";
import { useJiraTicketContext } from "~/hooks/useJiraTicketContext";
import { motion } from "motion/react";
import { SubField } from "~/components/Form/components";
import { JiraFormattedDestination } from "~/pages/integrations/hosted-integrations/forms/jira/JiraFormattedDestination";
import { getIntegrationMrn } from "~/pages/integrations/utils/integrationLinksUtils";

type CaseSettingsIntegrationProps = {
  integration: TicketIntegration;
  space: Space;
  showAutoCreateSection: boolean;
};

export const TicketSettingsJiraIntegration = ({
  integration,
  space,
  showAutoCreateSection,
}: CaseSettingsIntegrationProps) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const { control, handleSubmit, reset, watch } = useForm<JiraFormInput>({
    defaultValues: {
      defaultProject: "",
      autoCloseTickets: false,
      autoCreateTickets: false,
      cloudId: "",
    },
    mode: "onChange",
  });
  const { projects, isProjectsEmpty } = useJiraTicketContext({
    scopeMrn: integration?.mrn || "",
    skip: !integration?.mrn,
  });

  const [updateIntegration] = useUpdateClientIntegrationConfigurationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    refetchQueries: [
      {
        query: GetClientIntegrationDocument,
        variables: {
          mrn: getIntegrationMrn({
            scope: space,
            integrationId: integration?.mrn.split("/").pop() || "",
          }),
        },
      },
    ],
  });

  const onSubmit = async (data: JiraFormInput) => {
    const jiraConfigurationOptions = {
      host: data.host.trim(),
      email: data.email.trim(),
      apiToken: "",
      autoCloseTickets: data.autoCloseTickets,
      autoCreateCases: data.autoCreateTickets,
      defaultProject: data.defaultProject,
      cloudId: data.cloudId?.trim() || undefined,
    };

    await updateIntegration({
      variables: {
        input: {
          mrn: getIntegrationMrn({
            scope: space,
            integrationId: integration?.mrn.split("/").pop() || "",
          }),
          type: ClientIntegrationType.TicketSystemJira,
          configurationOptions: {
            jiraConfigurationOptions,
          },
        },
      },
    });
  };

  useEffect(() => {
    // TypeScript users
    const subscription = watch(() => {
      isInitialized && handleSubmit(onSubmit)();
    });
    return () => subscription.unsubscribe();
  }, [handleSubmit, watch, isInitialized]);

  const [watchAutoCreate] = watch(["autoCreateTickets"]);

  useEffect(() => {
    const configOptions = integration?.configurationOptions;
    if (configOptions?.__typename !== "JiraConfigurationOptions") return;
    reset({
      host: configOptions.host,
      email: configOptions.email,
      autoCloseTickets: configOptions.autoCloseTickets,
      autoCreateTickets: configOptions.autoCreateCases,
      defaultProject: configOptions.defaultProject,
      cloudId: configOptions.cloudId || "",
    });
    setIsInitialized(true);
  }, []);

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <JiraAutoConfigurationSection
          control={control}
          ticketType={IntegrationType.TicketSystemJira}
          showAutoCreateSection={showAutoCreateSection}
          autoCreateSubField={
            <Box
              component={motion.div}
              initial={false}
              transition={{
                duration: 0.35,
                staggerChildren: 0.25,
              }}
              sx={{ overflow: "hidden" }}
            >
              <Controller
                name="defaultProject"
                {...{ control }}
                render={({ field }) => (
                  <SubField
                    configuration="Select a drift destination"
                    caption="Select where to create an issue for each new drift ticket."
                    disabled={!watchAutoCreate}
                    field={
                      <Grid
                        sx={{ alignSelf: "flex-end" }}
                        size={{
                          xs: 12,
                          md: 6,
                        }}
                      >
                        <Select
                          {...field}
                          fullWidth
                          disabled={!watchAutoCreate}
                          sx={{
                            "&.MuiInputBase-root": {
                              bgcolor: "code.background",
                            },
                          }}
                          displayEmpty
                          renderValue={(selected: ReactNode) => {
                            const selectedProject = projects?.find(
                              (project) => project.key === selected,
                            );

                            const selectedProjectTitle = (
                              <JiraFormattedDestination
                                projectKey={selectedProject?.key || ""}
                                projectTitle={selectedProject?.name || ""}
                              />
                            );

                            if (isProjectsEmpty) {
                              return (
                                <>
                                  No projects available – create one in Jira
                                  first
                                </>
                              );
                            }

                            if (!selected || !selectedProjectTitle) {
                              return <>Select project</>;
                            }

                            return <>{selectedProjectTitle}</>;
                          }}
                        >
                          {(projects || []).map((project) => (
                            <MenuItem key={project.key} value={project.key}>
                              ({project.key}) {project.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                    }
                  />
                )}
              />
            </Box>
          }
        />
      </Paper>
    </Box>
  );
};
